// src/components/pages/shared/FiltrosEmpresas.tsx
//
// Botón "Filtros" con su panel desplegable. Lo usan la portada (Login) y
// Explorar. El estado vive en la página; aquí solo se pinta y se notifica.

import { useTranslation } from 'react-i18next';
import { SlidersHorizontal, Star, MapPin, CalendarCheck, Check, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select';

// Radios del filtro de cercanía, en km. El segundo es el que se aplica al
// activar la opción.
export const RADIOS_KM = [5, 10, 25];

// Nota mínima para considerar una empresa "mejor valorada".
export const NOTA_MINIMA = 4;

export interface FiltrosEmpresasValor {
  categoria: string | null;
  subcategoria: string | null;
  soloValoradas: boolean;
  soloReservaOnline: boolean;
  radioKm: number | null;
}

export const FILTROS_VACIOS: FiltrosEmpresasValor = {
  categoria: null,
  subcategoria: null,
  soloValoradas: false,
  soloReservaOnline: false,
  radioKm: null,
};

export interface OpcionSlug {
  slug: string;
  nombre: string;
}

/** Normaliza un texto a slug para usarlo como clave de filtro. */
export const aSlug = (texto: string) =>
  texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

/** Subcategorías disponibles dentro de una categoría, sin repetidos. */
export const subcategoriasDe = (empresas: any[], categoriaSlug: string | null): OpcionSlug[] => {
  if (!categoriaSlug) return [];
  const mapa = new Map<string, string>();
  empresas
    .filter((e) => e?.categoria_slug === categoriaSlug)
    .forEach((e) => {
      (e?.subcategorias ?? []).forEach((nombre: string) => {
        if (!nombre) return;
        const slug = aSlug(nombre);
        if (slug && !mapa.has(slug)) mapa.set(slug, nombre);
      });
    });
  return Array.from(mapa, ([slug, nombre]) => ({ slug, nombre }));
};

/** Aplica los filtros a una lista. No ordena: eso lo decide cada página. */
export const aplicarFiltros = <T extends Record<string, any>>(
  empresas: T[],
  f: FiltrosEmpresasValor
): T[] => {
  let lista = empresas;

  if (f.categoria) lista = lista.filter((e) => e?.categoria_slug === f.categoria);

  if (f.subcategoria) {
    lista = lista.filter((e) =>
      (e?.subcategorias ?? []).some((sc: string) => aSlug(sc) === f.subcategoria)
    );
  }

  // Reservar online exige las dos cosas: que la empresa lo tenga activado y
  // que su plan lo incluya. Si no, el usuario entra y no puede reservar.
  if (f.soloReservaOnline) {
    lista = lista.filter((e) => e?.reservas_online_citas && e?.plan_incluye_reservas);
  }

  if (f.radioKm != null) {
    lista = lista.filter((e) => e?.distancia != null && e.distancia <= f.radioKm!);
  }

  if (f.soloValoradas) {
    lista = lista.filter((e) => Number(e?.valoracion_media_empresa ?? 0) >= NOTA_MINIMA);
  }

  return lista;
};

/** Filtros activos del panel (la categoría se cuenta solo si el panel la muestra). */
export const contarFiltros = (f: FiltrosEmpresasValor, incluirCategoria: boolean) =>
  (incluirCategoria && f.categoria ? 1 : 0) +
  (f.subcategoria ? 1 : 0) +
  (f.soloValoradas ? 1 : 0) +
  (f.soloReservaOnline ? 1 : 0) +
  (f.radioKm != null ? 1 : 0);

const claseChip = (activo: boolean) =>
  `inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors ${
    activo
      ? 'border-transparent bg-[#288E7F] text-white'
      : 'border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-white/15 dark:text-gray-300 dark:hover:bg-white/10'
  }`;

const claseOpcion = (activo: boolean) =>
  `flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-sm transition-colors hover:bg-gray-50 dark:hover:bg-white/10 ${
    activo ? 'font-medium text-[#288E7F]' : 'text-gray-700 dark:text-gray-300'
  }`;

const claseSelect = 'bg-white dark:bg-[#1c1c1e] dark:border-white/15';

const Rotulo = ({ children }: { children: React.ReactNode }) => (
  <p className="pb-1.5 text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
    {children}
  </p>
);

const Separador = () => <div className="my-1 h-px bg-gray-100 dark:bg-white/10" />;

interface Props {
  valor: FiltrosEmpresasValor;
  onChange: (valor: FiltrosEmpresasValor) => void;
  /** Si se pasa, el panel muestra el desplegable de categoría. */
  categorias?: OpcionSlug[];
  subcategorias: OpcionSlug[];
  tieneUbicacion: boolean;
  /** Se llama al activar "Cerca de mí" sin ubicación concedida. */
  onPedirUbicacion: () => void;
  className?: string;
}

const FiltrosEmpresas = ({
  valor,
  onChange,
  categorias,
  subcategorias,
  tieneUbicacion,
  onPedirUbicacion,
  className = '',
}: Props) => {
  const { t } = useTranslation();
  const conCategoria = !!categorias?.length;
  const numFiltros = contarFiltros(valor, conCategoria);

  const cambiar = (parte: Partial<FiltrosEmpresasValor>) => onChange({ ...valor, ...parte });

  const alternarCercania = () => {
    if (valor.radioKm != null) {
      cambiar({ radioKm: null });
      return;
    }
    if (!tieneUbicacion) {
      onPedirUbicacion();
      return;
    }
    cambiar({ radioKm: RADIOS_KM[1] });
  };

  const limpiar = () =>
    onChange({
      ...FILTROS_VACIOS,
      // La categoría solo se limpia si se gestiona desde este panel.
      categoria: conCategoria ? null : valor.categoria,
    });

  return (
    <div className={className}>
      <Popover>
        <PopoverTrigger asChild>
          <button type="button" className={claseChip(numFiltros > 0)}>
            <SlidersHorizontal className="w-3.5 h-3.5" />
            {t('login.filters.button')}
            {numFiltros > 0 && (
              <span className="rounded-full bg-white/25 px-1.5 text-[10px] font-semibold">
                {numFiltros}
              </span>
            )}
          </button>
        </PopoverTrigger>

        <PopoverContent
          align="start"
          className="w-64 max-h-[70vh] overflow-y-auto p-2 bg-white dark:bg-[#1c1c1e] border-gray-200 dark:border-white/10"
        >
          {conCategoria && (
            <div className="px-2 pt-1 pb-2">
              <Rotulo>{t('login.filters.category')}</Rotulo>
              <Select
                value={valor.categoria ?? ''}
                onValueChange={(v) => cambiar({ categoria: v || null, subcategoria: null })}
              >
                <SelectTrigger className={claseSelect} />
                <SelectContent>
                  <SelectItem value="">{t('login.filters.allCategories')}</SelectItem>
                  {categorias!.map((c) => (
                    <SelectItem key={c.slug} value={c.slug}>
                      {c.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {subcategorias.length > 0 && (
            <div className="px-2 pt-1 pb-2">
              <Rotulo>{t('login.filters.subcategory')}</Rotulo>
              <Select
                value={valor.subcategoria ?? ''}
                onValueChange={(v) => cambiar({ subcategoria: v || null })}
              >
                <SelectTrigger className={claseSelect} />
                <SelectContent>
                  <SelectItem value="">{t('login.filters.allSubcategories')}</SelectItem>
                  {subcategorias.map((sc) => (
                    <SelectItem key={sc.slug} value={sc.slug}>
                      {sc.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {(conCategoria || subcategorias.length > 0) && <Separador />}

          <button
            type="button"
            onClick={() => cambiar({ soloValoradas: !valor.soloValoradas })}
            aria-pressed={valor.soloValoradas}
            className={claseOpcion(valor.soloValoradas)}
          >
            <Star className="w-4 h-4 shrink-0" />
            <span className="flex-1 text-left">{t('login.filters.topRated')}</span>
            {valor.soloValoradas && <Check className="w-4 h-4 shrink-0" />}
          </button>

          <button
            type="button"
            onClick={alternarCercania}
            aria-pressed={valor.radioKm != null}
            className={claseOpcion(valor.radioKm != null)}
          >
            <MapPin className="w-4 h-4 shrink-0" />
            <span className="flex-1 text-left">{t('login.filters.nearMe')}</span>
            {valor.radioKm != null && <Check className="w-4 h-4 shrink-0" />}
          </button>

          {valor.radioKm != null && (
            <div className="flex gap-2 pl-9 pr-2 pb-1">
              {RADIOS_KM.map((km) => (
                <button
                  key={km}
                  type="button"
                  onClick={() => cambiar({ radioKm: km })}
                  aria-pressed={valor.radioKm === km}
                  className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${
                    valor.radioKm === km
                      ? 'border-transparent bg-[#103254] text-white dark:bg-white dark:text-[#103254]'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-white/15 dark:text-gray-300 dark:hover:bg-white/10'
                  }`}
                >
                  {t('login.filters.radius', { km })}
                </button>
              ))}
            </div>
          )}

          <button
            type="button"
            onClick={() => cambiar({ soloReservaOnline: !valor.soloReservaOnline })}
            aria-pressed={valor.soloReservaOnline}
            className={claseOpcion(valor.soloReservaOnline)}
          >
            <CalendarCheck className="w-4 h-4 shrink-0" />
            <span className="flex-1 text-left">{t('login.filters.online')}</span>
            {valor.soloReservaOnline && <Check className="w-4 h-4 shrink-0" />}
          </button>

          {numFiltros > 0 && (
            <>
              <Separador />
              <button
                type="button"
                onClick={limpiar}
                className="flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-sm text-gray-500 transition-colors hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-white/10"
              >
                <X className="w-4 h-4 shrink-0" />
                {t('login.filters.clear')}
              </button>
            </>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default FiltrosEmpresas;